import { Directive, OnDestroy, AfterViewInit, NgZone, Injector, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RouterStateTrackerService } from '../services/router-state-tracker.service';
import { ThemeService } from '../services/theme.service';
import { CanComponentDeactivate } from '../auth/page-exit-animation.guard';
import { AuthService } from '../services/auth.service';
import { AuthGuard } from '../auth/auth.guard';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfigService } from '../provider/config.service';
import { TranslateService } from '@ngx-translate/core';

@Directive()
export abstract class BasePage implements AfterViewInit, OnDestroy, CanComponentDeactivate {
  // Animation class properties
  animClassTop: string = 'enterTop';
  animClassBottom: string = 'enterBottom';
  animClassAppear: string = 'enterAppear';
  animClassFade: string = 'enterFade';
  animClassQuick: string = 'enterQuick';
  animClassBounce: string = 'enterBounce';
  smallHeader: string = `smallHeader ${this.animClassTop}`;
  bigHeader: string = `bigHeader ${this.animClassTop}`;
  overLapped = false;
  
  // Settings and Subscriptions
  public appSettingsSubscription?: Subscription;
  public settings?: any;
  private authSubscription?: Subscription;
  
  // Destroy subject for takeUntil pattern
  protected destroy$ = new Subject<void>();
  
  // Scroll optimization properties
  private scrollRAF: number | null = null;
  private lastScrollState: boolean | null = null;
  private scrollObserver?: IntersectionObserver;
  
  // Configuration properties - can be overridden in child classes
  protected useIntersectionObserver: boolean = true; // Set to false to use scroll events
  protected animationDuration: number = 300; // milliseconds
  protected stickyElementId: string = 'stickyElement';
  protected scrollThreshold: number = -80; // pixels
  protected initialHeaderSize: 'bigHead' | 'smallHead' | 'biggerHead' | 'smallerHead' | 'tinyHead' | 'calendarHead' | 'slideHead' | 'menuScroll' = 'bigHead';
  protected scrolledHeaderSize: 'bigHead' | 'smallHead' | 'biggerHead' | 'smallerHead' | 'tinyHead' | 'calendarHead' | 'slideHead' | 'menuSmallScroll' = 'smallHead';

  // Services - Injected via Injector
  protected routerStateTrackerService?: RouterStateTrackerService;
  protected themeService?: ThemeService;
  protected ngZone?: NgZone;
  protected baseCdRef?: ChangeDetectorRef;
  
  protected translate: TranslateService;
  protected configService: ConfigService;

  _exitAnimationPlayed = false;

  constructor(
    protected injector: Injector,
  ) {
    this.routerStateTrackerService = this.injector.get(RouterStateTrackerService);
    this.themeService = this.injector.get(ThemeService);
    this.ngZone = this.injector.get(NgZone);
    this.baseCdRef = this.injector.get(ChangeDetectorRef);

    this.configService = this.injector.get(ConfigService);
    this.translate = this.injector.get(TranslateService);

    this.overLapped = false;
    this.lastScrollState = null;
    
  }
  protected getRouter(): Router | undefined {
    return this.injector.get(Router, undefined);
  }
  protected getActivatedRoute(): ActivatedRoute | undefined {
    return this.injector.get(ActivatedRoute, undefined);
  }
  protected getAuthService(): AuthService | undefined {
    return this.injector.get(AuthService, undefined);
  }
  async ngAfterViewInit(): Promise<void> {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        this.resetToEnterAnimations();
        this._exitAnimationPlayed = false;
        this.overLapped = false;
        this.themeService?.updateHeaderSize(this.initialHeaderSize);
        this.routerStateTrackerService?.registerActivePage(this);
        
        this.ngZone?.runOutsideAngular(() => {
          if (this.useIntersectionObserver) {
            this.setupScrollObserver();
          }
          
          setTimeout(() => {
            if (this.routeHasAuthGuard()) {
              this.ngZone?.run(() => this.startAuthMonitoring());
            }
          }, 100);
          
          setTimeout(() => {
            this.ngZone?.run(() => this.onPageReady());
          }, 50);
          
          setTimeout(() => {
            this.triggerInitialScrollCheck();
          }, 500);
        });
        
        this.baseCdRef?.detectChanges();
        resolve();
      });
    });
  }

  private triggerInitialScrollCheck(): void {
    const element = document.getElementById(this.stickyElementId);
    if (element) {
      const ttop = element.getBoundingClientRect();
      const isstuck = ttop.top <= this.scrollThreshold;
      this.ngZone?.run(() => {
        this.overLapped = isstuck;
        this.themeService?.updateHeaderSize(
          isstuck ? this.scrolledHeaderSize : this.initialHeaderSize
        );
        this.baseCdRef?.detectChanges();
      });
    } else {
      this.ngZone?.run(() => {
        this.overLapped = false;
        this.themeService?.updateHeaderSize(this.initialHeaderSize);
        this.baseCdRef?.detectChanges();
      });
    }
  }
  ngOnDestroy(): void {
    // Emit destroy signal to complete all takeUntil subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Cancel any pending RAF
    if (this.scrollRAF !== null) {
      cancelAnimationFrame(this.scrollRAF);
      this.scrollRAF = null;
    }
    
    // Disconnect IntersectionObserver
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = undefined;
    }

    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }

    if (this.appSettingsSubscription) {
      this.appSettingsSubscription.unsubscribe();
    }

    this.routerStateTrackerService?.unregisterActivePage(this);
    this.onPageDestroy();
  }
  
  protected setupScrollObserver(retryCount: number = 0): void {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = undefined;
    }

    const element = document.getElementById(this.stickyElementId);
    if (!element) {
      if (retryCount < 10) {
        setTimeout(() => this.setupScrollObserver(retryCount + 1), 100);
      }
      return;
    }
    
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        this.ngZone?.run(() => {
          const isstuck = !entries[0].isIntersecting;
          this.overLapped = isstuck;
          this.themeService?.updateHeaderSize(
            isstuck ? this.scrolledHeaderSize : this.initialHeaderSize
          );
          this.baseCdRef?.detectChanges();
        });
      },
      {
        threshold: [0, 1],
        rootMargin: `${this.scrollThreshold}px 0px 0px 0px`
      }
    );
    
    this.scrollObserver.observe(element);
  }
  public async playPreExitAnimation(): Promise<void> {
    if (this._exitAnimationPlayed) {
      return;
    }
    this._exitAnimationPlayed = true;

    return new Promise<void>((resolve) => {
      this.ngZone?.run(() => {
        this.animClassTop = 'exitTop';
        this.smallHeader = `smallHeader ${this.animClassTop}`;
        this.bigHeader = `bigHeader ${this.animClassTop}`;
        this.animClassBottom = 'exitBottom';
        this.animClassAppear = 'exitAppear';
        this.animClassFade = 'exitFade';
        this.animClassQuick = 'exitQuick';
        this.animClassBounce = 'exitBounce';
        
        this.themeService?.updateHeaderSize('smallHead');
        
        this.baseCdRef?.detectChanges();
      });
      
      this.ngZone?.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          const animatedElement = document.querySelector('.exitTop, .exitBottom, .exitAppear, .exitFade, .exitQuick, .exitBounce');
          
          if (animatedElement) {
            const onAnimationEnd = () => {
              animatedElement.removeEventListener('animationend', onAnimationEnd);
              this.ngZone?.run(() => resolve());
            };
            
            animatedElement.addEventListener('animationend', onAnimationEnd, { once: true });
            
            setTimeout(() => {
              animatedElement.removeEventListener('animationend', onAnimationEnd);
              this.ngZone?.run(() => resolve());
            }, this.animationDuration + 50);
          } else {
            setTimeout(() => {
              this.ngZone?.run(() => resolve());
            }, this.animationDuration);
          }
        });
      });
    });
  }
  public handleScroll(_event: any): void {
    // Cancel previous RAF if still pending (debounce)
    if (this.scrollRAF !== null) {
      cancelAnimationFrame(this.scrollRAF);
    }
    
    // Schedule read in next frame (synced with browser paint)
    this.scrollRAF = requestAnimationFrame(() => {
      this.ngZone?.runOutsideAngular(() => {
        const element = document.getElementById(this.stickyElementId);
        if (!element) {
          console.warn(`Element with id '${this.stickyElementId}' not found.`);
          return;
        }
        
        const ttop = element.getBoundingClientRect();
        const isstuck = ttop.top <= this.scrollThreshold;
        
        if (this.lastScrollState !== isstuck) {
          this.lastScrollState = isstuck;
          
          this.ngZone?.run(() => {
            this.overLapped = isstuck;
            this.themeService?.updateHeaderSize(
              isstuck ? this.scrolledHeaderSize : this.initialHeaderSize
            );
            this.baseCdRef?.detectChanges();
          });
        }
      });
      
      this.scrollRAF = null;
    });
  }
  public navigateToPage(page: string, data: any = {}, queryParams?: any): void {
    if (this.routerStateTrackerService) {
      const navigationOptions = queryParams ? { queryParams } : undefined;
      this.routerStateTrackerService.navigateWithPreExitAnimation(page, data, navigationOptions).catch((error) => {
        console.error('BasePage navigation error:', error);
        const router = this.getRouter();
        if (router) {
          router.navigate([page], navigationOptions).catch((routerError) => {
            console.error('Router fallback also failed:', routerError);
          });
        }
      });
    } else {
      console.warn('RouterStateTrackerService not available, trying direct router navigation');
      const router = this.getRouter();
      if (router) {
        const navigationOptions = queryParams ? { queryParams } : undefined;
        router.navigate([page], navigationOptions).catch((error) => {
          console.error('Direct router navigation failed:', error);
        });
      }
    }
  }
  protected onPageReady(): void {
    // Subscribe to app settings with takeUntil for automatic cleanup
    this.configService.appSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentSettings: any) => {
        this.settings = currentSettings;
        this.onSettingsChanged(currentSettings);
      });
  }

  protected onSettingsChanged(settings: any): void {
    // Child pages can override this to react to settings changes
  }
  protected onPageDestroy(): void {
  }
  private resetToEnterAnimations(): void {
    this.animClassTop = 'enterTop';
    this.animClassBottom = 'enterBottom';
    this.animClassAppear = 'enterAppear';
    this.animClassFade = 'enterFade';
    this.smallHeader = `smallHeader ${this.animClassTop}`;
    this.bigHeader = `bigHeader ${this.animClassTop}`;
  }
  private routeHasAuthGuard(): boolean {
    const activatedRoute = this.getActivatedRoute();
    if (!activatedRoute) {
      return false;
    }
    let route = activatedRoute.snapshot;
    while (route) {
      const routeConfig = route.routeConfig;
      if (routeConfig?.canActivate) {
        const hasAuthGuard = routeConfig.canActivate.some((guard: any) => {
          return guard === AuthGuard || guard.name === 'AuthGuard' || guard.constructor?.name === 'AuthGuard';
        });
        if (hasAuthGuard) {
          return true;
        }
      }
      route = route.parent as any;
    }
    return false;
  }
  private startAuthMonitoring(): void {
    const authService = this.getAuthService();
    if (!authService) {
      console.warn('AuthService not available for auth monitoring');
      return;
    }
    let hasSeenAuthenticated = false;
    this.authSubscription = authService.isAuthenticated$.subscribe((isAuthenticated: boolean) => {
      if (isAuthenticated) {
        hasSeenAuthenticated = true;
        return;
      }
      if (!hasSeenAuthenticated) {
        return;
      }
      setTimeout(() => {
        const router = this.getRouter();
        const currentUrl = router?.url || '';
        if (!currentUrl.includes('/onboarding')) {
          if (router) {
            router.navigateByUrl('/onboarding', { replaceUrl: true }).then((success) => {
              if (!success) {
                console.error('BasePage: Failed to redirect to onboarding');
              }
            }).catch((error) => {
              console.error('BasePage: Navigation error:', error);
            });
          } else {
            console.error('BasePage: Router not available');
          }
        }
      }, 100);
    });
  }
}